import { Link } from "react-router-dom";
import { DateTime as DT } from "luxon";
import ObjectAction from "./ObjectRows/ObjectAction";
import { formatPrice } from "utils/helpers/functions/formatPrice";
import {
    withBaseCurrency,
    withLocalCurrency,
    withPrimaryCurrency,
} from "utils/helpers/currency/currencyHelpers";
import useChangeDate from "hooks/useChangeDate";
import * as apiObject from "api/apiObjects";
import { useToast } from "context/ToastContext";
import { useTranslation } from "react-i18next";
import ObjectStatusIcon from "../ObjectStatus/ObjectStatusIcon";
import { useObject } from "../context/ObjectProvider";
import ObjectVat from "../ObjectVat/ObjectVat";
import FormInput from "components/forms/FormInput";
import { useState } from "react";
import { useSettings } from "context/SettingsContext";
import useSettingsParams from "hooks/useSettingsParams";
import ObjectPayStatus from "../ObjectPayStatus/ObjectPayStatus";
import { isObjectPaymentsCovered } from "../helpers/objectPayCoverage";
 
const ObjectsTableRows = ({vobject, fetchObjects, setClientInfoId, setTasksObjectId, onShowSpecification}) => {

    const { t } = useTranslation(['Objects']);
    const { showToast } = useToast();
    const { setShowSpinner, showDebtor, showClient } = useObject();
    const colSpan = 6 + (showClient ? 1 : 0) + (showDebtor ? 2 : 0);
    const { getSettings, localSettings } = useSettings();
    const params = useSettingsParams();
    const canManualPayStatus = Boolean(params.manualPayStatus);
    const dt = DT.fromMillis(vobject?.objectCreated||0).toLocaleString(DT.DATE_MED);
    const dtDeadline = DT.fromMillis(vobject?.objectDeadline||0).toLocaleString(DT.DATE_MED);
    const dtClassName = (DT.now().toMillis() > vobject?.objectDeadline) ? 'text-danger' : 'text-muted';
    const rate = Number(localSettings?.RATE?.current ?? 1) || 1;
    const isUahMode = rate === 1;
    const price = formatPrice(vobject?.objectPrice);
    const priceRate = formatPrice(vobject?.objectPriceRate);
    const paidUsd = Number(vobject?.paidUsd || 0);
    const paidUah = Number(vobject?.paidUah || 0);
    const paymentsCovered = isObjectPaymentsCovered({
        objectPrice: vobject?.objectPrice,
        objectPriceRate: vobject?.objectPriceRate,
        paidUsd,
        paidUah,
        rate,
    });
    const paymentsColorClass = paymentsCovered ? 'text-success' : 'text-warning';
    const { changeDate } = useChangeDate(); 

    const [ showFormName, setShowFormName ] = useState(false);

    const handleChangeDeadLine = async (newDeadLine) =>{ 
        setShowSpinner(true);
        const resChangeDeadLine = await apiObject.setDeadLine(vobject.ID, newDeadLine);
        await fetchObjects(true);
        if (resChangeDeadLine) showToast(t('messDeadLineChanged'), 'success')
                else showToast(t('common:messError'), 'danger');
    }

    const handleClickDate = async (newCreatedDate) => {
        setShowSpinner(true);
        const resDate = await apiObject.setCreated(vobject.ID, newCreatedDate);
        await fetchObjects(true);
        if (!resDate) showToast(t('common:messError'), 'danger');
    }

    const handleSetName = async (newObjectName) => {
        setShowSpinner(true);
        const resName = await apiObject.setName(vobject.ID, newObjectName);
        await getSettings();
        if (!resName) showToast(t('common:messError'), 'danger');
        await fetchObjects(true);
    }

    const handlePayStatusChange = async (payStatus) => {
        setShowSpinner(true);
        const res = await apiObject.setPayStatus(vobject.ID, payStatus);
        await fetchObjects(true);
        if (res) showToast(t('messPayStatusChanged'), 'success');
        else showToast(t('common:messError'), 'danger');
    };
 
    return (<>
        <tr>
            <td className="status">                
                 <ObjectStatusIcon status={vobject.objectStatus}/>     
            </td> 
            <td className="name">
                <Link to={`/objects/${vobject.ID}`}>
                <h6 className="mb-1 cursor-pointer text-break"> 
                    <span className="me-1">{vobject.objectName || vobject.clientName} </span>
                        {vobject.objectPower>0 && (<span className="d-inline-block fs-12 text-success">({vobject.objectPower} {t('labelKvt')})</span>)}
                        <div className="d-inline-block d-md-none"><ObjectVat isVat={vobject.objectVat}/></div>
                        </h6>
                {!showClient ? (
                    <p className={`f-12 mb-0 my-1 ${vobject.clientName ? 'text-muted' : 'text-warning'}`}>
                        {vobject.clientName || t('noLeadLabel')}
                    </p>
                ) : null}
                </Link>
            </td>
            {showClient ? (
                <td className="client">
                    {vobject.clientId ? (
                        <>
                            <h6
                                className="mb-1 text-break cursor-pointer"
                                onClick={() => setClientInfoId(vobject.clientId)}
                            >
                                {vobject.clientName || '-'}
                            </h6>
                            <p className="text-muted f-12 mb-0 my-1">{vobject.clientPhone || '-'}</p>
                        </>
                    ) : (
                        <span className="text-warning f-12">{t('noLeadLabel')}</span>
                    )}
                </td>
            ) : null}
            <td className={`team ${vobject.teamId ? '' : 'text-danger'}`}>
                {vobject.teamName}
            </td>
            <td className="price text-end"> 
                <h6 className="mb-1 cursor-pointer text-break d-flex justify-content-end">
                    <div><ObjectVat isVat={vobject.objectVat}/></div>
                    {withPrimaryCurrency(price, rate)}
                </h6>
                {!isUahMode && (
                    <p className="text-muted f-12 mb-0 my-1">{withLocalCurrency(priceRate)}</p>
                )}
            </td>
            {showDebtor ? (
                <>
                    <td className={`payments text-end ${paymentsColorClass}`}>
                        {paidUsd !== 0 || paidUah !== 0 ? (
                            <>
                                {paidUsd !== 0 ? (
                                    <h6 className={`mb-1 text-break ${paymentsColorClass}`}>
                                        {withBaseCurrency(formatPrice(paidUsd))}
                                    </h6>
                                ) : null}
                                {paidUah !== 0 ? (
                                    <p className={`f-12 mb-0 my-1 ${paymentsColorClass}`}>
                                        {withLocalCurrency(formatPrice(paidUah))}
                                    </p>
                                ) : null}
                            </>
                        ) : (
                            <span className={`f-12 ${paymentsColorClass}`}>—</span>
                        )}
                    </td>
                    <td className="pay-status text-center">
                        <ObjectPayStatus
                            objectId={vobject.ID}
                            payStatus={vobject.objectPayStatus}
                            objectPrice={vobject.objectPrice}
                            objectPriceRate={vobject.objectPriceRate}
                            paidUsd={paidUsd}
                            paidUah={paidUah}
                            rate={rate}
                            editable={canManualPayStatus}
                            onChange={handlePayStatusChange}
                        />
                    </td>
                </>
            ) : null}
            <td className="created text-end">
                <h6 className="mb-1 cursor-pointer text-break"
                    onClick={(e)=> {
                        if (e.ctrlKey)
                            changeDate(vobject?.objectCreated, handleClickDate)}
                    }>{dt}</h6> 
                <p  className={`${dtClassName} fw-medium fs-12 mb-0 my-1 text-truncate cursor-pointer hover-text-primary`}
                    title={t('toolDeadline')}
                    onClick={()=>
                        changeDate(vobject?.objectDeadline, handleChangeDeadLine)}>{dtDeadline}</p> 
            </td>
            <td className="action">
                <ObjectAction 
                    setShowFormName={setShowFormName}
                    setClientInfoId={setClientInfoId}
                    setTasksObjectId={setTasksObjectId}
                    vobject={vobject}
                    onShowSpecification={onShowSpecification}
                    fetchObjects={fetchObjects}/>
            </td>
          
        </tr>
        {showFormName &&
        (<tr>
            <td colSpan={colSpan}>
                 <div className="mb-3">
                    <FormInput 
                        require={true}
                        title={t('labelName')}
                        showForm={showFormName}
                        setShowForm={setShowFormName}
                        callBack={handleSetName}
                        defaultValue={vobject.objectName || ''}/>
                </div>
            </td>
        </tr>)}
    </>)
}

export default ObjectsTableRows
